import dayjs from "lib/dayjs";
import { dayCountFraction, freqToPeriodsPerYear } from "lib/dayCount";
import { enabledRefundingSolutionType } from "./refundingSolutionTypes";

const finite = (value) => Number.isFinite(Number(value)) ? Number(value) : 0;

export function buildRefundingStats({ form = {}, bondRows = [], comparison = [], discountRate = null } = {}) {
  const entries = Array.isArray(form.refunded_issues) ? form.refunded_issues : [];
  const maturities = entries.filter((entry) => entry?.entry_type === "ds_issue")
    .flatMap((entry) => Array.isArray(entry.selected_maturities) ? entry.selected_maturities : []);
  if (!maturities.length || !comparison.length) return null;

  const settlement = dayjs(form.deliveryDate || form.delivery_date);
  const refundedPar = maturities.reduce((sum, row) => sum + finite(row.principal), 0);
  const refundingPar = bondRows.reduce((sum, row) => sum + finite(row.principal), 0);
  const priorDebtService = comparison.reduce((sum, row) => sum + finite(row.priorDebtService), 0);
  const refundingDebtService = comparison.reduce((sum, row) => sum + finite(row.refundingDebtService), 0);
  const savings = priorDebtService - refundingDebtService;
  const annualRate = discountRate == null ? null : Number(discountRate);
  const compounding = freqToPeriodsPerYear(form.couponFrequency || form.coupon_frequency || "semiannual");
  const canDiscount = settlement.isValid() && Number.isFinite(annualRate) && annualRate > -compounding;
  const pvCashFlowSavings = canDiscount && comparison.every((row) => dayjs(row.date).isValid())
    ? comparison.reduce((sum, row) => {
        const years = Math.max(0, dayCountFraction(settlement, row.date, form.dayCount || form.day_count || "30/360"));
        return sum + finite(row.savings) / Math.pow(1 + annualRate / compounding, compounding * years);
      }, 0)
    : null;
  const averageRefundedCoupon = refundedPar > 0
    ? maturities.reduce((sum, row) => {
        const coupon = finite(row.coupon);
        return sum + finite(row.principal) * (coupon > 1 ? coupon / 100 : coupon);
      }, 0) / refundedPar
    : null;
  const refundedAverageLife = refundedPar > 0 && settlement.isValid()
    ? maturities.reduce((sum, row) => {
        const years = Math.max(0, dayjs(row.maturity_date).diff(settlement, "day") / 365.25);
        return sum + finite(row.principal) * years;
      }, 0) / refundedPar
    : null;

  return {
    solutionType: enabledRefundingSolutionType(form.refundingSolutionType || entries[0]?.solution_type),
    refundedPar,
    refundingPar,
    averageRefundedCoupon,
    refundedAverageLife,
    priorDebtService,
    refundingDebtService,
    grossCashFlowSavings: savings,
    discountRate: canDiscount ? annualRate : null,
    pvCashFlowSavings,
    preliminaryNetPvSavings: pvCashFlowSavings,
    preliminarySavingsPctOfRefundedPar: pvCashFlowSavings != null && refundedPar > 0
      ? pvCashFlowSavings / refundedPar
      : null,
    negativeSavingsPeriods: comparison.filter((row) => finite(row.savings) < -0.01).length,
  };
}
